import { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "katex/dist/katex.min.css";

// Matches the placeholder link href markdown uses to embed a
// video, e.g. [Vault Contact](youtube:9JkAYjsXV7o?start=53&end=243). The
// "youtube:" prefix isn't a real protocol — it just tells us which video id
// (and optional start/end clip params, in seconds) to embed.
const YOUTUBE_LINK_HREF = /^youtube:([a-zA-Z0-9_-]{6,15})(?:\?(.*))?$/;

// Forces every literal newline in the source to render as a visible break,
// using CommonMark's own hard-break rule (two trailing spaces before a line
// ending) rather than a remark plugin. Safe to apply to every line ending,
// including ones already inside a blank line: CommonMark defines a blank
// line as one containing only spaces/tabs (so "  " is still blank), and it
// explicitly ignores a hard-break marker on the last line of a paragraph —
// so this can't turn an intended paragraph break into something else, or
// add a stray <br> right before one.
function withHardBreaks(text) {
  if (typeof text !== "string") return text;
  return text.replace(/\r\n|\r|\n/g, "  \n");
}

// Renders a responsive 16:9 YouTube iframe for a youtube: link. start/end
// are optional clip bounds in seconds (from the ?start=&end= query params).
function YoutubeEmbed({ videoId, start, end }) {
  const params = new URLSearchParams();
  if (start) params.set("start", start);
  if (end) params.set("end", end);
  const query = params.toString();
  const src = `https://www.youtube-nocookie.com/embed/${videoId}${
    query ? `?${query}` : ""
  }`;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        paddingBottom: "56.25%", // 16:9 aspect ratio
        height: 0,
        margin: "8px 0",
        borderRadius: 6,
        overflow: "hidden",
        background: "#000",
      }}
    >
      <iframe
        src={src}
        title={`YouTube video ${videoId}`}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          border: 0,
        }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
      />
    </div>
  );
}

function rehypeSuperscript() {
  return (tree) => {
    function walk(node) {
      if (!node.children) return;
      for (let i = 0; i < node.children.length; i++) {
        const child = node.children[i];
        if (child.type === "text" && child.value.includes("^")) {
          const regex = /\^([^\s^]+)/g;
          const value = child.value;
          const parts = [];
          let lastIndex = 0;
          let match;
          while ((match = regex.exec(value)) !== null) {
            if (match.index > lastIndex) {
              parts.push({
                type: "text",
                value: value.slice(lastIndex, match.index),
              });
            }
            parts.push({
              type: "element",
              tagName: "sup",
              properties: {},
              children: [{ type: "text", value: match[1] }],
            });
            lastIndex = match.index + match[0].length;
          }
          if (parts.length > 0) {
            if (lastIndex < value.length) {
              parts.push({ type: "text", value: value.slice(lastIndex) });
            }
            node.children.splice(i, 1, ...parts);
            i += parts.length - 1;
          }
        } else {
          walk(child);
        }
      }
    }
    walk(tree);
  };
}

function splitWithHighlights(text, nodeStart, highlights) {
  const nodeEnd = nodeStart + text.length;
  const overlapping = highlights.filter(
    (h) => h.start < nodeEnd && h.end > nodeStart,
  );
  if (overlapping.length === 0) return [{ type: "text", value: text }];

  const cuts = new Set([0, text.length]);
  overlapping.forEach((h) => {
    const localStart = Math.max(0, h.start - nodeStart);
    const localEnd = Math.min(text.length, h.end - nodeStart);
    cuts.add(localStart);
    cuts.add(localEnd);
  });
  const sortedCuts = [...cuts].sort((a, b) => a - b);

  return sortedCuts.slice(0, -1).map((segStart, i) => {
    const segEnd = sortedCuts[i + 1];
    const segText = text.slice(segStart, segEnd);
    const absStart = nodeStart + segStart;
    const absEnd = nodeStart + segEnd;
    const hit = overlapping.find((h) => h.start <= absStart && h.end >= absEnd);
    if (hit) {
      return {
        type: "element",
        tagName: "span",
        properties: { style: `background-color: ${hit.mode}` },
        children: [{ type: "text", value: segText }],
      };
    }
    return { type: "text", value: segText };
  });
}

function makeRehypeHighlight(highlights) {
  return () => (tree) => {
    if (!highlights || highlights.length === 0) return;

    let charOffset = 0;

    function processChildren(parent) {
      if (!parent.children) return;
      const newChildren = [];
      for (const child of parent.children) {
        if (child.type === "text") {
          const nodeStart = charOffset;
          charOffset += child.value.length;
          const segments = splitWithHighlights(
            child.value,
            nodeStart,
            highlights,
          );
          newChildren.push(...segments);
        } else {
          processChildren(child);
          newChildren.push(child);
        }
      }
      parent.children = newChildren;
    }

    processChildren(tree);
  };
}

const BR_TAG = /^<br\s*\/?>$/i;

function rehypeBrTags() {
  return (tree) => {
    function walk(node) {
      if (!node.children) return;
      node.children = node.children.map((child) => {
        if (child.type === "raw" && BR_TAG.test(child.value.trim())) {
          return {
            type: "element",
            tagName: "br",
            properties: {},
            children: [],
          };
        }
        walk(child);
        return child;
      });
    }
    walk(tree);
  };
}

// scale(px) converts a base-14px value to the effective size for the current mode.
// Boost mode:   scale = (px) => round(px * multiplier)
// Override mode: scale = (px) => round((px / 14) * overridePx)  — proportional to override base
//                or identity if the override doesn't specify a font size
function makeComponents(scale) {
  return {
    h1: ({ children }) => (
      <h1
        style={{
          fontSize: scale(20),
          fontWeight: 700,
          margin: "16px 0 8px",
          color: "#1e3a8a",
          borderBottom: "1px solid #e5e7eb",
          paddingBottom: 4,
        }}
      >
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2
        style={{
          fontSize: scale(17),
          fontWeight: 700,
          margin: "14px 0 6px",
          color: "#1e40af",
        }}
      >
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3
        style={{
          fontSize: scale(15),
          fontWeight: 600,
          margin: "12px 0 4px",
          color: "#374151",
        }}
      >
        {children}
      </h3>
    ),
    // A real <p> is invalid inside a heading wrapper (as="h2" etc., which only
    // permits phrasing content). A span with display:block looks and lays out
    // identically to a <p> but is phrasing content itself, so it stays valid
    // no matter what tag the wrapper renders as.
    p: ({ children }) => (
      <span style={{ display: "block", margin: "0 0 10px" }}>{children}</span>
    ),
    ul: ({ children }) => (
      <ul style={{ margin: "0 0 10px", paddingLeft: 22 }}>{children}</ul>
    ),
    ol: ({ children }) => (
      <ol style={{ margin: "0 0 10px", paddingLeft: 22 }}>{children}</ol>
    ),
    li: ({ children }) => <li style={{ marginBottom: 4 }}>{children}</li>,
    strong: ({ children }) => (
      <strong style={{ fontWeight: 700 }}>{children}</strong>
    ),
    blockquote: ({ children }) => (
      <blockquote
        style={{
          borderLeft: "3px solid #93c5fd",
          margin: "8px 0",
          paddingLeft: 12,
          color: "#4b5563",
          fontStyle: "italic",
        }}
      >
        {children}
      </blockquote>
    ),
    a: ({ href, children }) => {
      const ytMatch = typeof href === "string" && href.match(YOUTUBE_LINK_HREF);
      if (ytMatch) {
        const [, videoId, queryString] = ytMatch;
        const params = new URLSearchParams(queryString || "");
        return (
          <YoutubeEmbed
            videoId={videoId}
            start={params.get("start")}
            end={params.get("end")}
          />
        );
      }
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#1e40af", textDecoration: "underline" }}
        >
          {children}
        </a>
      );
    },
    hr: () => (
      <hr
        style={{
          border: "none",
          borderTop: "1px solid #e5e7eb",
          margin: "12px 0",
        }}
      />
    ),
    img: ({ src, alt }) => (
      <img
        src={src}
        alt={alt || ""}
        style={{ maxWidth: "100%", borderRadius: 6, margin: "8px 0" }}
      />
    ),
    table: ({ children }) => (
      <table
        style={{
          borderCollapse: "collapse",
          width: "100%",
          marginBottom: 10,
          fontSize: scale(13),
        }}
      >
        {children}
      </table>
    ),
    th: ({ children }) => (
      <th
        style={{
          padding: "6px 10px",
          background: "#1e3a8a",
          color: "white",
          textAlign: "left",
          border: "1px solid #1e40af",
        }}
      >
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td style={{ padding: "6px 10px", border: "1px solid #e5e7eb" }}>
        {children}
      </td>
    ),
    code: ({ inline, children }) =>
      inline ? (
        <code
          style={{
            background: "#f3f4f6",
            padding: "1px 5px",
            borderRadius: 3,
            fontSize: scale(12),
            fontFamily: "monospace",
          }}
        >
          {children}
        </code>
      ) : (
        <pre
          style={{
            background: "#f3f4f6",
            padding: "10px 14px",
            borderRadius: 6,
            overflowX: "auto",
            fontSize: scale(12),
            fontFamily: "monospace",
          }}
        >
          <code>{children}</code>
        </pre>
      ),
    sup: ({ children }) => (
      <sup
        style={{
          fontSize: "0.75em",
          lineHeight: 0,
          position: "relative",
          verticalAlign: "baseline",
          top: "-0.4em",
        }}
      >
        {children}
      </sup>
    ),
  };
}

const BASE_PX = 12;

const WRAPPER_BASE = {
  all: "initial",
  display: "block",
  fontFamily: "inherit",
  color: "inherit",
  wordBreak: "break-word",
};

export default function MarkdownPreview({
  children,
  overrideStyle,
  fontSizeBoost,
  highlights = [],
  as: Wrapper = "div",
  preserveLineBreaks = false,
  ...rest
}) {
  const { components, wrapperStyle } = useMemo(() => {
    if (overrideStyle) {
      const overridePx = overrideStyle.fontSize
        ? parseFloat(overrideStyle.fontSize)
        : null;
      const scale = (px) => overridePx;
      return {
        components: makeComponents(scale),
        wrapperStyle: { ...WRAPPER_BASE, ...overrideStyle },
      };
    }

    // Boost mode: derive every size from BASE_PX * multiplier so body text
    // and explicit element sizes all scale together.
    const multiplier = fontSizeBoost / 100;
    const scale = (px) => Math.round(px * multiplier);
    return {
      components: makeComponents(scale),
      wrapperStyle: {
        ...WRAPPER_BASE,
        lineHeight: "1.5",
        fontSize: scale(BASE_PX),
      },
    };
  }, [fontSizeBoost, overrideStyle]);

  const rehypeHighlight = useMemo(
    () => makeRehypeHighlight(highlights),
    [highlights],
  );
  // NOTE: highlights' start/end are character offsets into the *original*
  // string. withHardBreaks inserts characters (two spaces per line ending),
  // so if this is ever combined with highlights on the same call, those
  // offsets will land in the wrong place. Fine today — no current caller
  // passes both — but don't turn on preserveLineBreaks for a call site that
  // also passes highlights without remapping the offsets first.
  const content = preserveLineBreaks ? withHardBreaks(children) : children;

  return (
    <Wrapper style={wrapperStyle} {...rest}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath]}
        rehypePlugins={[
          rehypeBrTags,
          rehypeKatex,
          rehypeSuperscript,
          rehypeHighlight,
        ]}
        urlTransform={(url) => url}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </Wrapper>
  );
}